import { Tooltip } from 'antd';
import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';

import { cn } from '../../../lib/cn';
import { Badge } from '../../../components/ui';
import { useT } from '../../../i18n';
import { NHAN_LOAI } from '../../cau-hinh-ocr/constants';
import type { DocumentType } from '../../cau-hinh-ocr/types';
import { STAGE_CONFIG, STATUS_CONFIG } from '../constants';
import { LIVE_STAGES, type DocBase, type DocStatus, type LiveStage } from '../types';

/** Nhãn loại chứng từ: phân loại, nên chỉ `neutral`. */
export function TypeBadge({ type }: { type: DocumentType }) {
  const { t } = useT();
  return <Badge variant="neutral">{t(NHAN_LOAI[type])}</Badge>;
}

export function StatusBadge({ status }: { status: DocStatus }) {
  const { t } = useT();
  const config = STATUS_CONFIG[status];
  return <Badge variant={config.variant}>{t(config.khoa)}</Badge>;
}

const laGiaiDoanDangChay = (s: string | null): s is LiveStage => (LIVE_STAGES as readonly string[]).includes(s ?? '');

/**
 * Ô trạng thái của chứng từ: đang xử lý thì mở rộng thành giai đoạn của lượt quét (nhãn, thanh
 * tiến trình, lượt thử); lỗi thì kèm bước lỗi và câu lỗi của backend ở tooltip.
 */
export function OcrStageBadge({ doc }: { doc: Pick<DocBase, 'status' | 'ocr_stage' | 'ocr_attempt' | 'ocr_error' | 'ocr_error_step'> }) {
  const { t } = useT();

  if (doc.status === 'PROCESSING' && laGiaiDoanDangChay(doc.ocr_stage)) {
    const stage = STAGE_CONFIG[doc.ocr_stage];
    const choThuLai = doc.ocr_stage === 'RETRY_WAIT';
    return (
      <Tooltip
        placement="left"
        title={
          <span className="text-xs">
            {choThuLai ? doc.ocr_error ?? t('chungTu.tooltipChoThuLai') : t('chungTu.tooltipGiaiDoan', { nhan: t(stage.khoa) })}
          </span>
        }
      >
        <div className="min-w-[150px] max-w-[220px]">
          <Badge variant={stage.variant}>
            {choThuLai ? <ExclamationCircleOutlined /> : <LoadingOutlined spin />}
            {t(stage.khoa)}
          </Badge>
          <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-subtle" aria-hidden>
            <div
              className={cn('h-full rounded-full', choThuLai ? 'bg-warning-500' : 'bg-primary-500')}
              style={{ width: `${stage.phanTram}%` }}
            />
          </div>
          {doc.ocr_attempt && doc.ocr_attempt > 1 && (
            <p className="mt-1 truncate text-2xs text-content-muted">{t('chungTu.luotThu', { so: doc.ocr_attempt })}</p>
          )}
        </div>
      </Tooltip>
    );
  }

  if (doc.status === 'ERROR') {
    const buoc = doc.ocr_error_step === 'READ' ? t('chungTu.buocDoc') : t('chungTu.buocSuyLuan');
    return (
      <Tooltip placement="left" title={doc.ocr_error ? <span className="text-xs">{doc.ocr_error}</span> : undefined}>
        <div className="min-w-[150px]">
          <StatusBadge status="ERROR" />
          {doc.ocr_error_step && (
            <p className="mt-1 truncate text-2xs text-content-muted">{t('chungTu.loiOBuoc', { buoc })}</p>
          )}
        </div>
      </Tooltip>
    );
  }

  return <StatusBadge status={doc.status} />;
}
